import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
// eslint-disable-next-line import/no-unresolved
import WaIcon from "images/integrations/wa.png";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from 'uuid';
import CallCustomerData from './CallCustomerData';
import ButtonComponent from "../../components/ButtonMC";
import IconMC from "../../components/IconMC";
import useWhatsappCallActions from '../../hooks/calls/useWhatsappCallActions';
import { useWhatsappCallsRuntime } from '../../hooks/calls/WhatsappCallsRuntimeContext';
import { useButtonsDisabled } from "../../hooks/shared/ui/useButtonsDisabled";
import useRingtone from "../../hooks/shared/ui/useRingtone";
import useFonemaOnNotAnswer from "../../hooks/fonema/actions/useFonemaOnNotAnswer";
import useWhatsappOnAnswer from "../../hooks/calls/useWhatsappOnAnswer";
import useCallPermissionActions from '../../hooks/fonema/actions/useCallPermissionActions';
import { sharingInformationService } from "../../../services/sharingInformationService";
import useVoiceOutboundFrom from '../../hooks/voice/application/useVoiceOutboundFrom';
import useVoiceOutboundLineSipGate from '../../hooks/voice/application/useVoiceOutboundLineSipGate';
import CallLineSelector from './CallLineSelector';
import { showtoast } from '../../../util/toast';

/**
 * InitiateCall Component
 *
 * Single SIP line / legacy: permission was already checked by CallButton / header list.
 * Multi SIP lines: checks permission only after the user selects a line in the selector.
 *
 * @param {Object} props - Component props
 * @param {Object} props.customer - The customer object containing customer information
 * @param {function} props.onClose - Function to close the initiate call
 */
const OutgoingCallDialog = memo(({ customer, registerNumber, onClose }) => {
  const { t } = useTranslation('Fonema');
  const { t: tVoice } = useTranslation('Voice');
  const { provider } = useWhatsappCallsRuntime();
  const isVoiceProvider = provider === 'voice';
  const {
    enabledLines,
    selectedFrom,
    selectedAppId,
    selectedPlatformRetailerId,
    setSelectedFrom,
    requiresFromSelection,
    mustConfirmFrom,
    loading: linesLoading,
    noSipReadyLines,
    sipFailed
  } = useVoiceOutboundFrom({ enabled: isVoiceProvider });

  const {
    checkPermission,
    requestPermission,
    requestingPermission
  } = useCallPermissionActions();

  const showCallLineSelector = useMemo(
    () => isVoiceProvider && enabledLines.length > 1,
    [isVoiceProvider, enabledLines.length]
  );

  // State for controlling when to play the sound
  const [shouldPlaySound, setShouldPlaySound] = useState(false);
  // State for showing/hiding the "X" when hovering on the top right corner
  const [isCloseHoverVisible, setIsCloseHoverVisible] = useState(false);
  const hasDialedRef = useRef(false);
  const lineSipToastShownRef = useRef('');
  const checkPermissionRef = useRef(checkPermission);
  const [linePermission, setLinePermission] = useState(null);
  const [checkingLinePermission, setCheckingLinePermission] = useState(false);

  checkPermissionRef.current = checkPermission;

  // Prefer full number with country code (E.164); fallback for inbox payload
  const destinationPhone = customer?.phone_with_country_code || customer?.phone;
  const fromNumber = useMemo(() => {
    if (!isVoiceProvider) return registerNumber;
    if (selectedFrom) return selectedFrom;
    if (mustConfirmFrom || requiresFromSelection) return '';
    return registerNumber || '';
  }, [
    isVoiceProvider,
    registerNumber,
    selectedFrom,
    mustConfirmFrom,
    requiresFromSelection
  ]);

  const selectedLineScope = useMemo(() => ({
    appId: selectedAppId || undefined,
    platformRetailerId: selectedPlatformRetailerId || undefined
  }), [selectedAppId, selectedPlatformRetailerId]);

  const {
    checking: sipLineChecking,
    lineReady: sipLineReady,
    checked: sipLineChecked
  } = useVoiceOutboundLineSipGate({
    enabled: isVoiceProvider,
    fromE164: fromNumber
  });

  // UI Layer
  const { buttonsDisabled, disableButtons, setButtonsDisabled } = useButtonsDisabled();

  // Outgoing call sound (only when shouldPlaySound is true)
  useRingtone(shouldPlaySound ? '/sounds/outgoing_call.mp3' : null, {
    loop: true,
    volume: 0.5
  });

  const startActiveCall = useCallback((_callInfoData) => {
    sharingInformationService.setSubject({
      activeCall: true,
      callInfoData: _callInfoData
    });
    sharingInformationService.setSubject({});
    onClose?.();
  }, [onClose]);

  const callInfoData = useMemo(() => ({
    callId: uuidv4(),
    customer,
    callerNumber: destinationPhone,
    registerNumber: fromNumber || registerNumber,
    appId: selectedAppId || undefined,
    callDirection: 'outgoing'
  }), [customer, destinationPhone, fromNumber, registerNumber, selectedAppId]);

  // Hook for the onAnswer event
  useWhatsappOnAnswer({
    callInfoData,
    startActiveCall
  });

  // Hook for the onNotAnswer event
  useFonemaOnNotAnswer({
    callInfoData,
    onClose
  });

  // Actions Layer
  const { call, cancelCall } = useWhatsappCallActions({
    disableButtons,
    callInfoData
  });

  useEffect(() => {
    if (!isVoiceProvider || !sipLineChecked || sipLineReady || !fromNumber) return;
    if (lineSipToastShownRef.current === fromNumber) return;
    lineSipToastShownRef.current = fromNumber;
    showtoast(tVoice('errors.line_sip_not_ready'));
  }, [fromNumber, isVoiceProvider, sipLineChecked, sipLineReady, tVoice]);

  // Multi Voice: check permission only after an explicit selector choice (selectedAppId).
  // Do not depend on `checkPermission` identity — that races with the actions hook's
  // own outbound-from instance and can discard a successful 200.
  useEffect(() => {
    if (!isVoiceProvider || !mustConfirmFrom) {
      setLinePermission(null);
      setCheckingLinePermission(false);
      return undefined;
    }

    if (!customer || linesLoading || requiresFromSelection || !fromNumber || !selectedAppId) {
      setLinePermission(null);
      setCheckingLinePermission(false);
      return undefined;
    }

    let cancelled = false;
    setCheckingLinePermission(true);
    setLinePermission(null);
    hasDialedRef.current = false;

    const scope = {
      appId: selectedAppId,
      platformRetailerId: selectedPlatformRetailerId || undefined
    };

    checkPermissionRef.current(customer, scope)
      .then((result) => {
        if (cancelled) return;
        setLinePermission(result);
      })
      .finally(() => {
        if (!cancelled) setCheckingLinePermission(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    customer,
    fromNumber,
    isVoiceProvider,
    linesLoading,
    mustConfirmFrom,
    requiresFromSelection,
    selectedAppId,
    selectedPlatformRetailerId
  ]);

  const multiPermissionOk = Boolean(linePermission?.canStartCall);
  const multiPermissionBlocksCall = isVoiceProvider && mustConfirmFrom && (
    checkingLinePermission || !multiPermissionOk
  );

  // Multi lines are already tenant ∩ SIP; skip the secondary SIP gate for enablement.
  const sipBlocksCall = isVoiceProvider && !mustConfirmFrom && (
    sipLineChecking || !sipLineReady
  );

  const handleCall = useCallback(async () => {
    if (isVoiceProvider && (requiresFromSelection || !fromNumber || noSipReadyLines || sipFailed)) return;
    if (sipBlocksCall) return;
    if (multiPermissionBlocksCall) return;
    setShouldPlaySound(true);
    const ok = await call({
      to: destinationPhone,
      from: fromNumber,
      ...(selectedAppId ? { appId: selectedAppId } : {})
    });
    if (!ok) {
      setShouldPlaySound(false);
      setButtonsDisabled(false);
      hasDialedRef.current = false;
    }
  }, [
    call,
    destinationPhone,
    fromNumber,
    isVoiceProvider,
    multiPermissionBlocksCall,
    noSipReadyLines,
    requiresFromSelection,
    selectedAppId,
    setButtonsDisabled,
    sipBlocksCall,
    sipFailed
  ]);

  const handleRequestPermission = useCallback(async () => {
    await requestPermission(customer, { lineScope: selectedLineScope });
  }, [customer, requestPermission, selectedLineScope]);

  const handleCancelCall = useCallback(async () => {
    setShouldPlaySound(false);
    cancelCall();
    onClose?.();
  }, [cancelCall, onClose]);

  useEffect(() => {
    if (hasDialedRef.current) return;
    if (isVoiceProvider && (linesLoading || mustConfirmFrom || !fromNumber || noSipReadyLines || sipFailed)) {
      return;
    }
    if (sipBlocksCall) return;
    if (multiPermissionBlocksCall) return;
    hasDialedRef.current = true;
    handleCall();
  }, [
    fromNumber,
    handleCall,
    isVoiceProvider,
    linesLoading,
    multiPermissionBlocksCall,
    mustConfirmFrom,
    noSipReadyLines,
    sipBlocksCall,
    sipFailed
  ]);

  const callDisabled = Boolean(
    (isVoiceProvider && (
      requiresFromSelection
      || !fromNumber
      || linesLoading
      || noSipReadyLines
      || sipFailed
      || sipBlocksCall
    ))
    || multiPermissionBlocksCall
  );

  const showPermissionRequest = Boolean(
    isVoiceProvider
    && mustConfirmFrom
    && linePermission
    && !linePermission.canStartCall
    && !buttonsDisabled
    && !requiresFromSelection
  );

  return (
    <div
      className="tw-wrapper"
      style={{
        right: 8,
        top: 185,
        position: "fixed",
        outline: 'none',
        maxWidth: 360,
        minWidth: 280,
        margin: 0,
        zIndex: 9999
      }}
      tabIndex={-1}
      aria-label={t('outcoming_call')}
    >
      <div className="incoming-call-bar tw-bg-blue-950 tw-text-white tw-select-none tw-p-4 tw-rounded-xl tw-border-transparent tw-relative">
        <div
          className="tw-absolute tw-top-1 tw-right-1 tw-w-8 tw-h-8 tw-z-[10000]"
          onMouseEnter={() => setIsCloseHoverVisible(true)}
          onMouseLeave={() => setIsCloseHoverVisible(false)}
        >
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className={[
              "tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center tw-rounded-full",
              "tw-bg-blue-900/60 hover:tw-bg-blue-800/70 tw-text-white",
              "tw-text-[12px] tw-font-bold tw-transition-opacity tw-duration-150",
              isCloseHoverVisible ? "tw-opacity-100 tw-pointer-events-auto" : "tw-opacity-0 tw-pointer-events-none"
            ].join(" ")}
          >
            X
          </button>
        </div>
        <div className="tw-flex tw-flex-row tw-align-middle">
          <img src={WaIcon} alt="WhatsApp" className="tw-w-6 tw-h-6" />
          <span className="tw-ml-1 tw-text-blue-100 tw-text-sm">{t('outcoming_call')}</span>
        </div>
        <CallCustomerData customer={customer} phone={destinationPhone} />
        {showCallLineSelector && (
          <CallLineSelector
            enabledLines={enabledLines}
            selectedFrom={selectedFrom}
            onChange={setSelectedFrom}
            disabled={buttonsDisabled || requestingPermission}
          />
        )}
        {isVoiceProvider && (noSipReadyLines || sipFailed) && !linesLoading && (
          <p className="tw-mt-2 tw-mb-0 tw-text-xs tw-text-amber-200">
            {tVoice('errors.no_sip_ready_lines')}
          </p>
        )}
        {showPermissionRequest && (
          <>
            <div className="tw-w-full tw-mt-2 tw-p-2 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-flex tw-items-start tw-gap-2">
              <IconMC name="info-outline" size="base" className="tw-text-blue-500 tw-flex-shrink-0 tw-self-start" />
              <p
                className="tw-m-0 tw-flex-1 tw-self-start tw-text-left tw-text-[10px] tw-text-blue-500 tw-font-medium"
                style={{ lineHeight: 1.15 }}
              >
                {t('permissionInfo')}
              </p>
            </div>
            <div className="tw-flex tw-gap-1 tw-mt-2">
              <ButtonComponent
                text={t('requestPermissionText')}
                className="tw-w-full"
                onClick={handleRequestPermission}
                disabled={
                  requestingPermission
                  || !linePermission?.canRequestPermission
                  || requiresFromSelection
                  || !selectedAppId
                }
              />
            </div>
          </>
        )}
        {!showPermissionRequest && (
          <div className="tw-flex tw-gap-1 tw-mt-2">
            {!buttonsDisabled && (
              <ButtonComponent
                text={t('call')}
                className="tw-w-full"
                icon="phone-outline"
                iconPosition="left"
                iconClassName="!tw-size-[16px]"
                variant="SUCCESS"
                onClick={handleCall}
                disabled={callDisabled}
              />
            )}
            {buttonsDisabled && (
              <ButtonComponent
                text={t('end_call')}
                className="tw-w-full"
                icon="phone-call-end"
                iconPosition="left"
                iconClassName="!tw-size-[16px]"
                variant="DANGER"
                onClick={handleCancelCall}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
});

OutgoingCallDialog.displayName = "OutgoingCallDialog";

export default OutgoingCallDialog;
